import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const FALLBACK_RATE = 70; // 1 USD = 70 AFN fallback

export const useExchangeRate = create(
  persist(
    (set, get) => ({
      rate:        FALLBACK_RATE, // USD to AFN
      lastUpdated: null,
      isLoading:   false,
      error:       null,
      isManual:    false,

      // Fetch live rate from exchangerate-api (free, no key needed for basic)
      fetchRate: async () => {
        set({ isLoading: true, error: null });
        try {
          // Use free open exchange rate API
          const res = await fetch(
            'https://open.er-api.com/v6/latest/USD',
            { signal: AbortSignal.timeout(5000) }
          );
          if (!res.ok) throw new Error('API error');
          const data = await res.json();
          const afnRate = data.rates?.AFN;
          if (afnRate && afnRate > 0) {
            set({
              rate:        afnRate,
              lastUpdated: new Date().toISOString(),
              isLoading:   false,
              error:       null,
              isManual:    false,
            });
            return afnRate;
          }
          throw new Error('AFN rate not found');
        } catch (err) {
          // On error, keep last saved rate
          set({ isLoading: false, error: err.message });
          return get().rate; // return cached rate
        }
      },

      // Set manual rate
      setManualRate: (rate) => {
        const r = parseFloat(rate);
        if (r > 0) {
          set({ rate: r, isManual: true, lastUpdated: new Date().toISOString(), error: null });
        }
      },

      // Convert USD to AFN
      toAFN: (usd) => usd * get().rate,
      // Convert AFN to USD
      toUSD: (afn) => afn / get().rate,
      // Convert between currencies
      convert: (amount, from, to) => {
        if (from === to) return amount;
        if (from === 'USD' && to === 'AFN') return amount * get().rate;
        if (from === 'AFN' && to === 'USD') return amount / get().rate;
        return amount;
      },
    }),
    {
      name: 'exchange-rate',
      partialize: (s) => ({ rate: s.rate, lastUpdated: s.lastUpdated, isManual: s.isManual }),
    }
  )
);

export default useExchangeRate;
